/**
 * Nerd Wishlist - Theme Integration JavaScript
 *
 * Provides WishlistButton web component for adding/removing products from wishlist.
 * Compatible with Shopify Dawn theme patterns.
 *
 * @version 1.0.0
 */

'use strict';

(function() {
  /**
   * Get i18n string from theme settings with fallback
   * @param {string} key - String key from window.NerdWishlist.strings
   * @param {string} fallback - Fallback text if key not found
   * @returns {string}
   */
  const getString = (key, fallback = '') => {
    return window.NerdWishlist?.strings?.[key] || fallback;
  };

  /**
   * Get current locale from Shopify
   * @returns {string|null} Locale code (e.g., 'de', 'en') or null
   */
  const getCurrentLocale = () => {
    return window.Shopify?.locale || document.documentElement.lang || null;
  };

  /**
   * Build API URL with locale parameter for translated content
   * @param {string} path - API path (e.g., '/apps/wishlist/api/items')
   * @returns {string} Full URL with locale query parameter
   */
  const apiUrl = (path) => {
    const url = new URL(window.location.origin + path);
    const locale = getCurrentLocale();
    if (locale) url.searchParams.set('locale', locale);
    return url.toString();
  };
  /**
   * GuestWishlist - Local storage manager for guest wishlists
   * Scaffolded for Phase 5 implementation
   */
  class GuestWishlist {
    static STORAGE_KEY = 'nerd_wishlist_items';
    static MAX_ITEMS = 50;

    /**
     * Get all wishlist items from localStorage
     * @returns {number[]} Array of variant IDs
     */
    static getItems() {
      try {
        const data = localStorage.getItem(this.STORAGE_KEY);
        return data ? JSON.parse(data) : [];
      } catch (error) {
        console.error('[GuestWishlist] Failed to read from localStorage:', error);
        return [];
      }
    }

    /**
     * Check if variant is in wishlist
     * @param {string|number} variantId - Shopify variant ID
     * @returns {boolean}
     */
    static hasItem(variantId) {
      const items = this.getItems();
      return items.includes(Number(variantId));
    }

    /**
     * Add variant to wishlist
     * @param {string|number} variantId - Shopify variant ID
     * @returns {boolean} Success status
     */
    static addItem(variantId) {
      try {
        let items = this.getItems();
        const id = Number(variantId);

        if (items.includes(id)) {
          return true; // Already in list
        }

        items.push(id);

        // Enforce MAX_ITEMS limit (FIFO)
        if (items.length > this.MAX_ITEMS) {
          items = items.slice(-this.MAX_ITEMS);
        }

        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
        return true;
      } catch (error) {
        console.error('[GuestWishlist] Failed to add item:', error);
        return false;
      }
    }

    /**
     * Remove variant from wishlist
     * @param {string|number} variantId - Shopify variant ID
     * @returns {boolean} Success status
     */
    static removeItem(variantId) {
      try {
        const items = this.getItems();
        const id = Number(variantId);
        const filtered = items.filter(item => item !== id);

        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filtered));
        return true;
      } catch (error) {
        console.error('[GuestWishlist] Failed to remove item:', error);
        return false;
      }
    }
  }

  /**
   * NotificationToggle - Toggle for email notifications on purchased items
   * Only visible for logged-in users on the wishlist page
   */
  class NotificationToggle {
    constructor(container, options = {}) {
      this.container = container;
      this.enabled = options.enabled || false;

      if (!window.NerdWishlist?.isLoggedIn) {
        // Guest mode - don't render toggle
        return;
      }

      this.render();
      this.bindEvents();
    }

    render() {
      this.container.innerHTML = `
        <div class="notification-toggle">
          <label class="notification-toggle__label">
            <span class="notification-toggle__text">${getString('notifyToggleText', 'Notify me when someone buys something from my wishlist')}</span>
            <span class="notification-toggle__switch">
              <input type="checkbox" class="notification-toggle__input" ${this.enabled ? 'checked' : ''}>
              <span class="notification-toggle__slider"></span>
            </span>
          </label>
        </div>
      `;

      this.input = this.container.querySelector('.notification-toggle__input');
    }

    bindEvents() {
      if (!this.input) return;

      this.input.addEventListener('change', async (e) => {
        const enabled = e.target.checked;
        this.input.disabled = true;

        try {
          const response = await fetch(apiUrl('/apps/wishlist/api/notify/toggle'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ enabled })
          });

          if (!response.ok) {
            throw new Error('Failed to update notification preference');
          }

          const data = await response.json();
          this.enabled = data.notifyOnPurchase;

          // Show success toast
          document.dispatchEvent(new CustomEvent('wishlist:toast', {
            detail: {
              message: enabled ? getString('toastNotifyEnabled', 'Notifications enabled') : getString('toastNotifyDisabled', 'Notifications disabled'),
              duration: 3000
            }
          }));

        } catch (error) {
          console.error('[NotificationToggle] Error:', error);
          // Revert checkbox state on error
          this.input.checked = !enabled;

          document.dispatchEvent(new CustomEvent('wishlist:toast', {
            detail: {
              message: getString('toastNotifySaveError', 'Could not save setting'),
              duration: 3000
            }
          }));
        } finally {
          this.input.disabled = false;
        }
      });
    }
  }

  /**
   * ShareManager - Handles share link creation, display, and deactivation
   * Only active for logged-in customers
   */
  class ShareManager {
    constructor() {
      this.container = null;
      this.shareBtn = null;
      this.linkContainer = null;
      this.linkInput = null;
      this.copyBtn = null;
      this.deactivateBtn = null;
      this.hasActiveLink = false;
      this.currentToken = null;
    }

    /**
     * Initialize share manager if logged in
     */
    init() {
      // Skip if wishlist-section.liquid is managing share functionality
      if (window.NerdWishlistShareManaged) return;

      // Only init for logged-in users
      if (!window.NerdWishlist?.isLoggedIn) return;

      this.container = document.getElementById('wishlist-share');
      if (!this.container) return;

      this.shareBtn = document.getElementById('share-btn');
      this.linkContainer = document.getElementById('share-link-container');
      this.linkInput = document.getElementById('share-link-input');
      this.copyBtn = document.getElementById('share-copy-btn');
      this.deactivateBtn = document.getElementById('share-deactivate-btn');

      // Bind event listeners
      this.shareBtn?.addEventListener('click', () => this.handleShareClick());
      this.copyBtn?.addEventListener('click', () => this.handleCopyClick());
      this.deactivateBtn?.addEventListener('click', () => this.handleDeactivateClick());

      // Check for existing share link
      this.checkStatus();
    }

    /**
     * Check if customer already has an active share link
     */
    async checkStatus() {
      try {
        const response = await fetch(apiUrl('/apps/wishlist/api/share/status'));
        if (!response.ok) return;

        const data = await response.json();
        if (data.hasActiveLink) {
          this.hasActiveLink = true;
          this.currentToken = data.token;
          this.showLinkUI(data.url);
        }
      } catch (error) {
        console.error('[ShareManager] Failed to check status:', error);
      }
    }

    /**
     * Handle share button click - create link if needed
     */
    async handleShareClick() {
      if (this.hasActiveLink) {
        // Already have link, just show it
        this.linkContainer.style.display = 'flex';
        return;
      }

      this.shareBtn.disabled = true;
      this.shareBtn.textContent = '...';

      try {
        const response = await fetch(apiUrl('/apps/wishlist/api/share/create'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) throw new Error('Failed to create share link');

        const data = await response.json();
        this.hasActiveLink = true;
        this.currentToken = data.token;
        // Generate URL from token (create endpoint returns only token, not url)
        const shareUrl = `${window.location.origin}/apps/wishlist/share/${data.token}`;
        this.showLinkUI(shareUrl);

        document.dispatchEvent(new CustomEvent('wishlist:toast', {
          detail: { message: getString('toastShareCreated', 'Share link created!'), duration: 3000 }
        }));
      } catch (error) {
        console.error('[ShareManager] Failed to create link:', error);
        document.dispatchEvent(new CustomEvent('wishlist:toast', {
          detail: { message: getString('toastShareCreateError', 'Could not create link'), duration: 3000 }
        }));
      } finally {
        this.shareBtn.disabled = false;
        this.shareBtn.textContent = getString('btnShare', 'Share');
      }
    }

    /**
     * Copy share link to clipboard
     */
    async handleCopyClick() {
      const url = this.linkInput?.value;
      if (!url) return;

      try {
        // Modern clipboard API
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(url);
        } else {
          // Fallback for older browsers
          this.linkInput.select();
          document.execCommand('copy');
        }

        // Visual feedback
        const originalText = this.copyBtn.textContent;
        this.copyBtn.textContent = getString('btnCopied', 'Copied!');
        setTimeout(() => {
          this.copyBtn.textContent = originalText;
        }, 2000);

        document.dispatchEvent(new CustomEvent('wishlist:toast', {
          detail: { message: getString('toastLinkCopied', 'Link copied to clipboard!'), duration: 2000 }
        }));
      } catch (error) {
        console.error('[ShareManager] Copy failed:', error);
      }
    }

    /**
     * Deactivate share link
     */
    async handleDeactivateClick() {
      if (!confirm(getString('confirmDeactivate', 'Are you sure? The link will become invalid and cannot be restored.'))) {
        return;
      }

      this.deactivateBtn.disabled = true;

      try {
        const response = await fetch(apiUrl('/apps/wishlist/api/share/deactivate'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) throw new Error('Failed to deactivate');

        this.hasActiveLink = false;
        this.currentToken = null;
        this.hideLinkUI();

        document.dispatchEvent(new CustomEvent('wishlist:toast', {
          detail: { message: getString('toastShareDeactivated', 'Share link deactivated'), duration: 3000 }
        }));
      } catch (error) {
        console.error('[ShareManager] Deactivate failed:', error);
        document.dispatchEvent(new CustomEvent('wishlist:toast', {
          detail: { message: getString('toastShareDeactivateError', 'Deactivation failed'), duration: 3000 }
        }));
      } finally {
        this.deactivateBtn.disabled = false;
      }
    }

    /**
     * Show the link UI with URL
     */
    showLinkUI(url) {
      if (this.linkInput) this.linkInput.value = url;
      if (this.linkContainer) this.linkContainer.style.display = 'flex';
      if (this.shareBtn) this.shareBtn.textContent = getString('btnShowLink', 'Show link');
    }

    /**
     * Hide the link UI after deactivation
     */
    hideLinkUI() {
      if (this.linkContainer) this.linkContainer.style.display = 'none';
      if (this.linkInput) this.linkInput.value = '';
      if (this.shareBtn) this.shareBtn.textContent = getString('btnShare', 'Share');
    }
  }

  /**
   * WishlistModal - Modal for first-time wishlist addition (guests)
   */
  class WishlistModal {
    constructor() {
      this.overlay = null;
      this.modal = null;
    }

    /**
     * Show the wishlist modal
     * @param {Object} options - Modal options
     * @param {string} options.wishlistUrl - URL to wishlist page
     * @param {boolean} options.isGuest - Whether user is a guest (shows login hint)
     */
    show(options = {}) {
      const wishlistUrl = options.wishlistUrl || '/pages/nerd-wunschliste';
      const isGuest = options.isGuest ?? !window.NerdWishlist?.isLoggedIn;

      // Create overlay
      this.overlay = document.createElement('div');
      this.overlay.className = 'wishlist-modal-overlay';
      this.overlay.addEventListener('click', (e) => {
        if (e.target === this.overlay) this.close();
      });

      // Create modal
      this.modal = document.createElement('div');
      this.modal.className = 'wishlist-modal';
      this.modal.setAttribute('role', 'dialog');
      this.modal.setAttribute('aria-modal', 'true');
      this.modal.setAttribute('aria-labelledby', 'wishlist-modal-title');

      const hintHtml = isGuest
        ? `<p class="wishlist-modal__hint">${getString('modalGuestHint', 'Sign in or register to save your wishlist permanently.')}</p>`
        : '';

      this.modal.innerHTML = `
        <button class="wishlist-modal__close" aria-label="${getString('btnClose', 'Close')}">&times;</button>
        <div class="wishlist-modal__icon">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
        </div>
        <h2 id="wishlist-modal-title" class="wishlist-modal__title">${getString('modalTitle', 'Added to wishlist')}</h2>
        ${hintHtml}
        <div class="wishlist-modal__actions">
          <a href="${wishlistUrl}" class="wishlist-modal__btn wishlist-modal__btn--primary">${getString('btnWishlist', 'View wishlist')}</a>
          <button type="button" class="wishlist-modal__btn wishlist-modal__btn--secondary">${getString('btnContinue', 'Continue shopping')}</button>
        </div>
      `;

      this.overlay.appendChild(this.modal);
      document.body.appendChild(this.overlay);

      // Bind close events
      this.modal.querySelector('.wishlist-modal__close').addEventListener('click', () => this.close());
      this.modal.querySelector('.wishlist-modal__btn--secondary').addEventListener('click', () => this.close());

      // Trap focus and handle escape key
      document.addEventListener('keydown', this.handleKeydown.bind(this));

      // Animate in
      requestAnimationFrame(() => {
        this.overlay.classList.add('wishlist-modal-overlay--visible');
        this.modal.classList.add('wishlist-modal--visible');
      });

      // Focus the primary button
      setTimeout(() => {
        this.modal.querySelector('.wishlist-modal__btn--primary')?.focus();
      }, 100);
    }

    /**
     * Handle keyboard events
     */
    handleKeydown(e) {
      if (e.key === 'Escape') {
        this.close();
      }
    }

    /**
     * Close the modal
     */
    close() {
      if (!this.overlay) return;

      document.removeEventListener('keydown', this.handleKeydown.bind(this));

      this.overlay.classList.remove('wishlist-modal-overlay--visible');
      this.modal.classList.remove('wishlist-modal--visible');

      setTimeout(() => {
        if (this.overlay && this.overlay.parentNode) {
          this.overlay.parentNode.removeChild(this.overlay);
        }
        this.overlay = null;
        this.modal = null;
      }, 300);
    }
  }

  // Global modal instance
  const wishlistModal = new WishlistModal();

  /**
   * ToastManager - Accessible toast notification system
   */
  class ToastManager {
    constructor() {
      this.container = null;
      this.init();
    }

    /**
     * Initialize toast container with accessibility attributes
     */
    init() {
      // Create container if it doesn't exist
      if (!document.querySelector('.wishlist-toast-container')) {
        this.container = document.createElement('div');
        this.container.className = 'wishlist-toast-container';
        this.container.setAttribute('aria-live', 'polite');
        this.container.setAttribute('aria-atomic', 'true');
        this.container.style.cssText = `
          position: fixed;
          bottom: 20px;
          right: 20px;
          z-index: 9999;
          pointer-events: none;
        `;
        document.body.appendChild(this.container);
      } else {
        this.container = document.querySelector('.wishlist-toast-container');
      }

      // Listen for toast events
      document.addEventListener('wishlist:toast', (e) => {
        this.show(e.detail.message, e.detail.duration);
      });
    }

    /**
     * Show a toast notification
     * @param {string} message - Message to display
     * @param {number} duration - Duration in milliseconds (default: 3000)
     */
    show(message, duration = 3000) {
      if (!this.container) return;

      const toast = document.createElement('div');
      toast.className = 'wishlist-toast';
      toast.textContent = message;
      toast.style.cssText = `
        background: #333;
        color: #fff;
        padding: 12px 20px;
        border-radius: 4px;
        margin-bottom: 10px;
        opacity: 0;
        transform: translateY(20px);
        transition: opacity 0.3s ease, transform 0.3s ease;
        pointer-events: auto;
        max-width: 300px;
        word-wrap: break-word;
      `;

      this.container.appendChild(toast);

      // Trigger animation
      requestAnimationFrame(() => {
        toast.style.opacity = '1';
        toast.style.transform = 'translateY(0)';
      });

      // Auto-remove after duration
      setTimeout(() => {
        toast.style.opacity = '0';
        toast.style.transform = 'translateY(20px)';

        setTimeout(() => {
          if (toast.parentNode) {
            toast.parentNode.removeChild(toast);
          }
        }, 300);
      }, duration);
    }
  }

  /**
   * WishlistButton - Custom element for wishlist add/remove
   */
  class WishlistButton extends HTMLElement {
    constructor() {
      super();
      this.button = null;
      this.variantId = null;
      this.productId = null;
      this.shopDomain = null;
      this.customerId = null;
      this.form = null;
      this.priceElement = null;
      this.mutationObserver = null;
    }

    /**
     * Called when element is added to DOM
     */
    connectedCallback() {
      // Handle both document ready and already loaded states
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
    }

    /**
     * Called when element is removed from DOM
     */
    disconnectedCallback() {
      this.cleanup();
    }

    /**
     * Initialize the component
     */
    init() {
      // Get button reference (could be the element itself or a child)
      this.button = this.querySelector('button') || this;

      // Read data attributes
      this.variantId = this.dataset.variantId;
      this.productId = this.dataset.productId;
      this.shopDomain = this.dataset.shop;
      this.customerId = this.dataset.customerId || null;

      // Validate required attributes
      if (!this.variantId || !this.shopDomain) {
        console.error('[WishlistButton] Missing required data attributes: data-variant-id and data-shop');
        return;
      }

      // Load initial state
      this.loadState();

      // Attach event listeners
      this.button.addEventListener('click', this);

      // Setup variant change detection
      this.setupVariantChangeDetection();
    }

    /**
     * Cleanup event listeners and observers
     */
    cleanup() {
      if (this.button) {
        this.button.removeEventListener('click', this);
      }
      if (this.form) {
        this.form.removeEventListener('change', this);
      }
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
      }
      if (this.variantObserver) {
        this.variantObserver.disconnect();
      }
      if (this.variantPollInterval) {
        clearInterval(this.variantPollInterval);
      }
      window.removeEventListener('popstate', this);
    }

    /**
     * Event handler (Dawn pattern)
     * @param {Event} event
     */
    handleEvent(event) {
      if (event.type === 'click' && event.currentTarget === this.button) {
        this.toggleWishlist(event);
      } else if (event.type === 'change' && event.target.name === 'id') {
        this.handleVariantChange(event.target.value);
      } else if (event.type === 'popstate') {
        this.handleUrlChange();
      }
    }

    /**
     * Setup variant change detection (multiple strategies)
     */
    setupVariantChangeDetection() {
      // Strategy 1: Listen to form changes (works for both input and select)
      this.form = document.querySelector('form[action*="/cart/add"]');
      if (this.form) {
        this.form.addEventListener('change', this);
      }

      // Strategy 2: MutationObserver on the variant select/input element
      // This catches programmatic changes made by theme JS
      const variantSelect = this.form?.querySelector('select[name="id"], input[name="id"]');
      if (variantSelect) {
        this.variantObserver = new MutationObserver(() => {
          const newValue = variantSelect.value;
          if (newValue && newValue !== this.variantId && newValue !== 'not-selected') {
            this.handleVariantChange(newValue);
          }
        });
        // Observe attribute changes (for when value is set programmatically)
        this.variantObserver.observe(variantSelect, {
          attributes: true,
          attributeFilter: ['value']
        });

        // Also poll for changes since some themes change value without triggering events
        this.variantPollInterval = setInterval(() => {
          const newValue = variantSelect.value;
          if (newValue && newValue !== this.variantId && newValue !== 'not-selected') {
            this.handleVariantChange(newValue);
          }
        }, 500);
      }

      // Strategy 3: MutationObserver on price element
      // Many themes update price when variant changes
      this.priceElement = document.querySelector('.price, .product__price, [class*="price"]');
      if (this.priceElement) {
        this.mutationObserver = new MutationObserver(() => {
          const variantInput = this.form?.querySelector('select[name="id"], input[name="id"]');
          if (variantInput && variantInput.value !== this.variantId && variantInput.value !== 'not-selected') {
            this.handleVariantChange(variantInput.value);
          }
        });
        this.mutationObserver.observe(this.priceElement, {
          childList: true,
          characterData: true,
          subtree: true
        });
      }

      // Strategy 4: URL ?variant= parameter changes
      window.addEventListener('popstate', this);
    }

    /**
     * Handle variant change
     * @param {string} newVariantId
     */
    handleVariantChange(newVariantId) {
      if (newVariantId && newVariantId !== this.variantId) {
        this.variantId = newVariantId;
        this.dataset.variantId = newVariantId;
        this.dataset.variantSelected = 'true'; // Mark that user has selected a variant
        this.loadState();
      }
    }

    /**
     * Handle URL change (back/forward navigation)
     */
    handleUrlChange() {
      const params = new URLSearchParams(window.location.search);
      const variantId = params.get('variant');
      if (variantId && variantId !== this.variantId) {
        this.handleVariantChange(variantId);
      }
    }

    /**
     * Get the API base URL - use current hostname for custom domains
     */
    getApiBaseUrl() {
      // Use current hostname to support custom domains (e.g., www.getdigital.de)
      // This ensures App Proxy works correctly regardless of domain
      return window.location.origin;
    }

    /**
     * Load wishlist state for current variant
     */
    async loadState() {
      try {
        let inWishlist = false;

        if (this.customerId) {
          // Logged-in customer: Check via API
          // App Proxy automatically adds shop and logged_in_customer_id query params
          const response = await fetch(apiUrl('/apps/wishlist/api/items'), {
            method: 'GET',
            headers: {
              'Accept': 'application/json'
            },
            credentials: 'include'
          });

          if (response.ok) {
            const data = await response.json();
            const items = data.items || [];
            inWishlist = items.some(item =>
              String(item.variantId) === String(this.variantId) ||
              String(item.shopifyVariantId) === String(this.variantId)
            );
          }
        } else {
          // Guest: Check localStorage
          inWishlist = GuestWishlist.hasItem(this.variantId);
        }

        this.dataset.inWishlist = inWishlist ? 'true' : 'false';
      } catch (error) {
        console.error('[WishlistButton] Failed to load state:', error);
      }
    }

    /**
     * Check if variant selection is required but not yet made
     * @returns {boolean}
     */
    isVariantSelectionRequired() {
      // If product has only one variant, no selection needed
      if (this.dataset.variantRequired !== 'true') {
        return false;
      }

      // Check if variant is in URL (explicit selection)
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('variant')) {
        return false;
      }

      // Check if user has interacted with variant selectors
      if (this.dataset.variantSelected === 'true') {
        return false;
      }

      // Check theme's variant option selectors (data-variant-option-chosen-value)
      // All option selects must have a chosen value (not "false") for variant to be complete
      const variantSelects = document.querySelectorAll('select[data-variant-option]');
      if (variantSelects.length > 0) {
        const allChosen = Array.from(variantSelects).every(
          select => select.dataset.variantOptionChosenValue &&
                    select.dataset.variantOptionChosenValue !== 'false'
        );
        if (allChosen) {
          return false;
        }
      }

      return true;
    }

    /**
     * Toggle wishlist status
     * @param {Event} event
     */
    async toggleWishlist(event) {
      event.preventDefault();

      // Don't allow action while loading
      if (this.dataset.loading === 'true') {
        return;
      }

      // Check if variant selection is required for adding (not removing)
      const isAdding = this.dataset.inWishlist !== 'true';
      if (isAdding && this.isVariantSelectionRequired()) {
        document.dispatchEvent(new CustomEvent('wishlist:toast', {
          detail: {
            message: getString('toastVariantRequired', 'Please select a variant first.'),
            duration: 3000
          }
        }));
        return;
      }

      // Guest users: use localStorage (Phase 5 will enhance this)
      if (!this.customerId) {
        this.handleGuestToggle();
        return;
      }

      const currentState = this.dataset.inWishlist === 'true';
      const newState = !currentState;
      const action = newState ? 'add' : 'remove';
      // Use apiUrl helper for locale support and custom domain support
      const endpoint = apiUrl(`/apps/wishlist/api/${action}`);

      // Optimistic UI update
      this.dataset.inWishlist = newState ? 'true' : 'false';
      this.dataset.loading = 'true';
      this.button.disabled = true;

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            shopifyVariantId: this.variantId
          })
        });

        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }

        // Show modal when adding, toast when removing
        if (newState) {
          wishlistModal.show();
        } else {
          document.dispatchEvent(new CustomEvent('wishlist:toast', {
            detail: { message: getString('toastRemoved', 'Removed from wishlist'), duration: 3000 }
          }));
        }

        // Dispatch update event for other components
        document.dispatchEvent(new CustomEvent('wishlist:updated', {
          detail: {
            variantId: this.variantId,
            productId: this.productId,
            inWishlist: newState
          }
        }));

      } catch (error) {
        console.error('[WishlistButton] Toggle failed:', error);

        // Revert optimistic update on error
        this.dataset.inWishlist = currentState ? 'true' : 'false';

        // Show error toast
        document.dispatchEvent(new CustomEvent('wishlist:toast', {
          detail: {
            message: getString('toastError', 'Failed to update wishlist. Please try again.'),
            duration: 3000
          }
        }));

      } finally {
        this.dataset.loading = 'false';
        this.button.disabled = false;
      }
    }

    /**
     * Handle guest wishlist toggle (localStorage only)
     */
    handleGuestToggle() {
      const currentState = this.dataset.inWishlist === 'true';
      const newState = !currentState;

      // Check if variant selection is required for adding
      if (newState && this.isVariantSelectionRequired()) {
        document.dispatchEvent(new CustomEvent('wishlist:toast', {
          detail: {
            message: getString('toastVariantRequired', 'Please select a variant first.'),
            duration: 3000
          }
        }));
        return;
      }

      if (newState) {
        GuestWishlist.addItem(this.variantId);
      } else {
        GuestWishlist.removeItem(this.variantId);
      }

      this.dataset.inWishlist = newState ? 'true' : 'false';

      // Show modal when adding, toast when removing
      if (newState) {
        wishlistModal.show();
      } else {
        document.dispatchEvent(new CustomEvent('wishlist:toast', {
          detail: { message: getString('toastRemoved', 'Removed from wishlist'), duration: 3000 }
        }));
      }

      document.dispatchEvent(new CustomEvent('wishlist:updated', {
        detail: {
          variantId: this.variantId,
          productId: this.productId,
          inWishlist: newState
        }
      }));
    }
  }

  // Register custom element
  if (typeof customElements !== 'undefined') {
    customElements.define('wishlist-button', WishlistButton);
  }

  // Initialize ToastManager
  let toastManager;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      toastManager = new ToastManager();
      mergeGuestWishlistOnLogin();
    });
  } else {
    toastManager = new ToastManager();
    mergeGuestWishlistOnLogin();
  }

  // Initialize ShareManager
  let shareManager;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      shareManager = new ShareManager();
      shareManager.init();
    });
  } else {
    shareManager = new ShareManager();
    shareManager.init();
  }

  // Export NotificationToggle for use in wishlist page
  window.NotificationToggle = NotificationToggle;

  /**
   * Merge guest wishlist items when user logs in
   * Runs on every page load to catch login from any page
   * Requires wishlist-config.liquid snippet to be included in theme.liquid
   */
  async function mergeGuestWishlistOnLogin() {
    // Check if config is available (wishlist-config.liquid must be included)
    if (!window.NerdWishlist || !window.NerdWishlist.isLoggedIn) return;

    // Check if there are guest items to merge
    const guestItems = GuestWishlist.getItems();
    if (guestItems.length === 0) return;

    // Check if we already merged for this customer
    const mergeKey = `nerd_wishlist_merged_${window.NerdWishlist.customerId}`;
    if (sessionStorage.getItem(mergeKey)) return;

    try {
      const response = await fetch(apiUrl('/apps/wishlist/api/merge'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variantIds: guestItems.map(String) })
      });

      if (response.ok) {
        const data = await response.json();

        // Clear localStorage after successful merge
        localStorage.removeItem(GuestWishlist.STORAGE_KEY);

        // Mark as merged for this customer
        sessionStorage.setItem(mergeKey, 'true');

        // Show toast if items were merged
        if (data.merged > 0) {
          const message = getString('toastMerged', '✓ {count} items from your local wishlist have been transferred to your account.').replace('{count}', data.merged);
          document.dispatchEvent(new CustomEvent('wishlist:toast', {
            detail: {
              message: message,
              duration: 5000
            }
          }));
        }

        // Refresh any wishlist buttons on the page
        document.querySelectorAll('wishlist-button').forEach(btn => {
          if (btn.loadState) btn.loadState();
        });
      }
    } catch (error) {
      console.error('[Wishlist] Failed to merge guest wishlist:', error);
    }
  }

})();
